import { ViewConfiguration } from '../../../grid/gridView/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { ProductServiceStatus } from '../../../license/types/ProductServiceStatus';
import { MdrServiceFlavor } from '../../../license/types/MdrServiceFlavor';
import { translateFn } from '../../../utils/translator.helper';
import { DateConditions, IntEnumConditions, StringConditions } from '../../../grid/gridFilters/types';
import _ from 'lodash';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';

export interface MdrCompaniesGridWebComponentSchema {
  view: ViewConfiguration<Column, ColumnKeys>;
  columns: Array<ColumnDetails<Column, ColumnKeys>>;
  ssoIsAvailable: boolean;
}

export interface MdrCompanyGridItem {
  _id: string;
  companyName: string;
  companyType: MdrCompanyType;
  serviceStatus: ProductServiceStatus;
  serviceType: MdrServiceFlavor;
  startDate: string;
  endDate: string;
}

export interface GetDataGridItemsResponse {
  rawEvents?: Array<MdrCompanyGridItem>;
}

interface Filter {
  companyName: StringConditions;
  companyType: IntEnumConditions;
  serviceStatus: IntEnumConditions;
  serviceType: IntEnumConditions;
  startDate: DateConditions;
  endDate: DateConditions;
}

export interface SetFiltersRequest {
  viewId: string;
  filters: Array<Filter>;
}

export interface SsoAvailabilityResponse {
  success: boolean;
  isAvailable: boolean;
}

export enum ColumnKeys {
  MDR_COMPANY_NAME = 'companyName',
  MDR_COMPANY_TYPE = 'companyType',
  MDR_SERVICE_STATUS = 'serviceStatus',
  MDR_SERVICE_TYPE = 'serviceType',
  MDR_START_DATE = 'startDate',
  MDR_END_DATE = 'endDate',
}

export enum Column {
  MDR_COMPANY_NAME,
  MDR_COMPANY_TYPE,
  MDR_SERVICE_STATUS,
  MDR_SERVICE_TYPE,
  MDR_START_DATE,
  MDR_END_DATE,
}

export const DISPLAYED_FILTERS = [
  { [ColumnKeys.MDR_COMPANY_NAME]: { contains: '' } },
  { [ColumnKeys.MDR_COMPANY_TYPE]: { in: [] } },
  { [ColumnKeys.MDR_SERVICE_STATUS]: { in: [] } },
  { [ColumnKeys.MDR_SERVICE_TYPE]: { in: [] } },
];

export const PREDEFINED_FILTERS_KEYS = [
  ColumnKeys.MDR_COMPANY_NAME,
  ColumnKeys.MDR_COMPANY_TYPE,
  ColumnKeys.MDR_SERVICE_STATUS,
  ColumnKeys.MDR_SERVICE_TYPE,
];

export const DISPLAYED_COLUMNS = _.range(Object.keys(Column).length);

export const FILTER_TIME_RANGE = 730;

export function getColumnDetails(): ColumnDetails<Column, ColumnKeys>[] {
  return [
    {
      column: Column.MDR_COMPANY_NAME,
      key: ColumnKeys.MDR_COMPANY_NAME,
      columnName: translateFn('Company name'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      width: '280px',
    },
    {
      column: Column.MDR_COMPANY_TYPE,
      key: ColumnKeys.MDR_COMPANY_TYPE,
      columnName: translateFn('Company type'),
      type: ColumnType.ENUM,
      values: getMdrCompanyTypeValues(),
      removable: false,
      sortable: true,
      editingDisabled: true,
      width: '200px',
    },
    {
      column: Column.MDR_SERVICE_STATUS,
      key: ColumnKeys.MDR_SERVICE_STATUS,
      columnName: translateFn('MDR service status'),
      type: ColumnType.ENUM,
      values: getMdrServiceStatusValues(),
      removable: false,
      sortable: false,
      editingDisabled: true,
      width: '200px',
    },
    {
      column: Column.MDR_SERVICE_TYPE,
      key: ColumnKeys.MDR_SERVICE_TYPE,
      columnName: translateFn('MDR service model'),
      type: ColumnType.ENUM,
      values: getMdrServiceTypeValues(),
      removable: false,
      sortable: false,
      editingDisabled: true,
      width: '200px',
    },
    {
      column: Column.MDR_START_DATE,
      key: ColumnKeys.MDR_START_DATE,
      columnName: translateFn('Activation date'),
      type: ColumnType.DATE,
      removable: false,
      sortable: false,
      width: '210px',
      filterResetValue: {
        timeRange: {
          kind: TimeRangeKind.ALL_TO_DATE,
        },
      },
    },
    {
      column: Column.MDR_END_DATE,
      key: ColumnKeys.MDR_END_DATE,
      columnName: translateFn('End date'),
      type: ColumnType.DATE,
      removable: false,
      sortable: false,
      filterResetValue: {
        timeRange: {
          kind: TimeRangeKind.ALL_TO_DATE,
        },
      },
      flexGrow: 1,
    },
  ];
}

export function getMdrCompanyTypeValues() {
  return [
    {
      label: translateFn('Customer'),
      value: MdrCompanyType.CUSTOMER,
    },
    {
      label: translateFn('Partner'),
      value: MdrCompanyType.PARTNER,
    },
  ];
}

export enum MdrCompanyType {
  CUSTOMER,
  PARTNER,
}

export function getMdrServiceStatusValues() {
  return [
    {
      label: translateFn('Active'),
      value: ProductServiceStatus.ACTIVE,
    },
    {
      label: translateFn('Inactive'),
      value: ProductServiceStatus.INACTIVE,
    },
    {
      label: translateFn('Onboarding'),
      value: ProductServiceStatus.ONBOARDING,
    },
    {
      label: translateFn('Switching flavor'),
      value: ProductServiceStatus.SWITCHING,
    },
    {
      label: translateFn('Disabling'),
      value: ProductServiceStatus.DISABLING,
    },
    {
      label: translateFn('Enrolling'),
      value: ProductServiceStatus.ENROLLING,
    },
  ];
}

export function getMdrServiceTypeValues() {
  return [
    {
      label: translateFn('MDR Foundations for MSP'),
      value: MdrServiceFlavor.FOUNDATIONS,
    },
    {
      label: translateFn('MDR Premium'),
      value: MdrServiceFlavor.PREMIUM,
    },
    {
      label: translateFn('MDR Enterprise'),
      value: MdrServiceFlavor.ENTERPRISE,
    },
    {
      label: translateFn('MDR Foundations'),
      value: MdrServiceFlavor.FOUNDATIONS_LEGACY,
    },
    {
      label: translateFn('Bitdefender MDR'),
      value: MdrServiceFlavor.BITDEFENDER_MDR,
    },
    {
      label: translateFn('Bitdefender MDR Plus'),
      value: MdrServiceFlavor.BITDEFENDER_MDR_PLUS,
    },
  ];
}
